import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

import SEMANTIC_COLORS from '../../../theme/semantic-colors.json';

/**
 * Preset reference at {@link https://github.com/primefaces/primevue/tree/master/packages/themes/src/presets/aura}
 */
export const intellivendPreset = definePreset(Aura, {
  semantic: {
    ...SEMANTIC_COLORS,
    focusRing: {
      width: '0',
    },
    content: {
      borderRadius: '{border.radius.sm}',
    },
    formField: {
      borderRadius: '{border.radius.sm}',
    },
    layout: {
      header: {
        height: '3.75rem',
        background: '{secondary.800}',
      },
      page: {
        padding: '1.5rem',
      },
      sidebar: {
        background: '{secondary.900}',
        activeBackground: '{secondary.800}',
      },
    },
    disabled: {
      opacity: '0.5',
    },
    colorScheme: {
      light: {
        formField: {
          invalidBorderColor: '{red.500}',
          disabledBackground: '{surface.100}',
        },
      },
    },
  },
  components: {
    toast: {
      root: {
        borderWidth: '0 0 0 0.25rem',
      },
      icon: {
        size: '1.25rem',
      },
      colorScheme: {
        light: {
          success: {
            color: '{success.700}',
            borderColor: '{success.700}',
            background: 'color-mix(in srgb, {success.700}, #fff 95%)',
            shadow: '0 0 0.0625rem 0.0625rem color-mix(in srgb, {success.700}, transparent 75%)',
          },
          info: {
            color: '{info.700}',
            borderColor: '{info.700}',
            background: 'color-mix(in srgb, {info.700}, #fff 95%)',
            shadow: '0 0 0.0625rem 0.0625rem color-mix(in srgb, {info.700}, transparent 75%)',
          },
          warn: {
            color: '{warn.700}',
            borderColor: '{warn.700}',
            background: 'color-mix(in srgb, {warn.700}, #fff 95%)',
            shadow: '0 0 0.0625rem 0.0625rem color-mix(in srgb, {warn.700}, transparent 75%)',
          },
          error: {
            color: '{error.700}',
            borderColor: '{error.700}',
            background: 'color-mix(in srgb, {error.700}, #fff 95%)',
            shadow: '0 0 0.0625rem 0.0625rem color-mix(in srgb, {error.700}, transparent 75%)',
          },
        },
      },
    },
    paginator: {
      navButton: {
        borderRadius: '0.25rem',
        selectedBackground: '{primary.100}',
      },
    },
    datatable: {
      paginatorBottom: {
        borderWidth: '0',
      },
    },
    dialog: {
      header: {
        padding: '1.5rem',
        gap: '0.5rem',
      },
      title: {
        fontSize: '1.25rem',
      },
      content: {
        padding: '0 1.5rem 1.5rem 1.5rem',
      },
      footer: {
        padding: '0 1.5rem 1.5rem 1.5rem',
        gap: '0.5rem',
      },
    },
    progressbar: {
      root: {
        borderRadius: '0',
        height: '0.5rem',
      },
      value: {
        background: '{primary.500}',
      },
    },
    tabs: {
      tab: {
        borderWidth: '0 0 0.125rem 0',
      },
    },
    tag: {
      colorScheme: {
        light: {
          contrast: {
            color: '{gray.700}',
            background: 'transparent',
          },
        },
      },
    },
    card: {
      root: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 0 0 0.0625rem {content.border.color}',
      },
    },
    menu: {
      item: {
        padding: '0.75rem 1.25rem',
      },
    },
    radiobutton: {
      root: {
        checkedBackground: '{form.field.background}',
        checkedHoverBackground: '{form.field.background}',
      },
      icon: {
        checkedColor: '{primary.color}',
        checkedHoverColor: '{primary.color}',
      },
    },
    message: {
      content: {
        padding: '1rem',
        gap: '1rem',
      },
      text: {
        fontWeight: '500',
      },
      colorScheme: {
        light: {
          secondary: {
            color: '{secondary.800}',
            borderColor: '{secondary.100}',
            background: '{secondary.50}',
          },
          success: {
            color: '{success.700}',
            borderColor: '{success.100}',
            background: '{success.50}',
          },
          info: {
            color: '{info.700}',
            borderColor: '{info.100}',
            background: '{info.50}',
          },
          warn: {
            color: '{warn.700}',
            borderColor: '{warn.100}',
            background: '{warn.50}',
          },
          error: {
            color: '{error.700}',
            borderColor: '{error.100}',
            background: '{error.50}',
          },
        },
      },
    },
    avatar: {
      root: {
        background: '{primary.500}',
        color: '#FFF',
      },
    },
    button: {
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.500}',
              borderColor: '{primary.500}',
              hoverBackground: '{primary.600}',
              hoverBorderColor: '{primary.600}',
              activeBackground: '{primary.700}',
              activeBorderColor: '{primary.700}',
            },
            secondary: {
              color: '#fff',
              background: '{secondary.500}',
              borderColor: '{secondary.500}',
              hoverColor: '#fff',
              hoverBackground: '{secondary.600}',
              hoverBorderColor: '{secondary.600}',
              activeColor: '#fff',
              activeBackground: '{secondary.700}',
              activeBorderColor: '{secondary.700}',
            },
            info: {
              background: '{info.500}',
              borderColor: '{info.500}',
              hoverBackground: '{info.600}',
              hoverBorderColor: '{info.600}',
              activeBackground: '{info.700}',
              activeBorderColor: '{info.700}',
            },
            success: {
              background: '{success.500}',
              borderColor: '{success.500}',
              hoverBackground: '{success.600}',
              hoverBorderColor: '{success.600}',
              activeBackground: '{success.700}',
              activeBorderColor: '{success.700}',
            },
            warn: {
              background: '{warn.500}',
              borderColor: '{warn.500}',
              hoverBackground: '{warn.600}',
              hoverBorderColor: '{warn.600}',
              activeBackground: '{warn.700}',
              activeBorderColor: '{warn.700}',
            },
            danger: {
              background: '{error.500}',
              borderColor: '{error.500}',
              hoverBackground: '{error.600}',
              hoverBorderColor: '{error.600}',
              activeBackground: '{error.700}',
              activeBorderColor: '{error.700}',
            },
          },
          outlined: {
            primary: {
              color: '{primary.500}',
              hoverBackground: '{primary.500}',
              activeBackground: '{primary.600}',
              borderColor: '{primary.500}',
            },
            secondary: {
              color: '{secondary.500}',
              hoverBackground: '{secondary.500}',
              activeBackground: '{secondary.600}',
              borderColor: '{secondary.500}',
            },
            info: {
              color: '{info.500}',
              hoverBackground: '{info.500}',
              activeBackground: '{info.600}',
              borderColor: '{info.500}',
            },
            success: {
              color: '{success.500}',
              hoverBackground: '{success.500}',
              activeBackground: '{success.600}',
              borderColor: '{success.500}',
            },
            warn: {
              color: '{warn.500}',
              hoverBackground: '{warn.500}',
              activeBackground: '{warn.600}',
              borderColor: '{warn.500}',
            },
            danger: {
              color: '{error.500}',
              hoverBackground: '{error.500}',
              activeBackground: '{error.600}',
              borderColor: '{error.500}',
            },
          },
          text: {
            primary: {
              color: '{primary.500}',
              hoverBackground: '{primary.50}',
              activeBackground: '{primary.100}',
            },
            secondary: {
              color: '{secondary.500}',
              hoverBackground: '{secondary.50}',
              activeBackground: '{secondary.100}',
            },
            info: {
              color: '{info.500}',
              hoverBackground: '{info.50}',
              activeBackground: '{info.100}',
            },
            success: {
              color: '{success.500}',
              hoverBackground: '{success.50}',
              activeBackground: '{success.100}',
            },
            warn: {
              color: '{warn.500}',
              hoverBackground: '{warn.50}',
              activeBackground: '{warn.100}',
            },
            danger: {
              color: '{error.500}',
              hoverBackground: '{error.50}',
              activeBackground: '{error.100}',
            },
          },
        },
      },
    },
  },
});
